import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  Spinner,
} from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { getbranchData } from "../../../../redux/authentication";
import { getInventorySupplier } from "../../../../redux/inventory";
import PurchaseEntry from "./Bills/PurchaseEntry";

const formatDate = (dte) => {
  const now = new Date(dte);
  const istOffset = 5.5 * 60 * 60 * 1000;
  const istTime = new Date(now.getTime() + istOffset);
  const formattedDate = istTime.toISOString().split("T")[0];
  return formattedDate;
};

const PurchaseForm = ({
  editFormData = {},
  togglePurchaseEntry,
  mode = "Return Mode",
  itemsData,
}) => {
  const itemsList = useSelector((state) => state.inventory);
  const auth = useSelector((state) => state.authentication);
  const dispatch = useDispatch();
  const { userData } = auth;
  const { supplierData, purchaseData } = itemsList;
  const [error, setError] = useState("");
  const [errorDate, setErrorDate] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [datewithoutHours, setDatewithoutHours] = useState("");

  const registrationType = [
    { value: "", label: "Select Registration Type" },
    { value: "consumer", label: "Consumer" },
    { value: "regular", label: "Regular" },
    { value: "composition", label: "Composition" },
    { value: "unregistered", label: "Unregistered" },
  ];

  const [selectedDate, setSelectedDate] = useState(formatDate(new Date()));
  const [selectedRtnDate, setSelectedRtnDate] = useState(
    formatDate(new Date())
  );

  const validateDate = (dateString) => {
    if (typeof dateString !== "string") return false;
    const regex = /^(\d{4})-(\d{2})-(\d{2})$/;
    const match = dateString.match(regex);
    if (!match) return false;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);

    const date = new Date(year, month, day);
    return (
      date.getFullYear() === year &&
      date.getMonth() === month &&
      date.getDate() === day
    );
  };

  const formObj = {
    supplier: {
      supplierId: "",
      address: "",
      state: "",
      contactNo: "",
      partyName: "",
    },
    invoice: "",
    paymentDetails: {
      invoice_id: "",
      invoice_date: datewithoutHours,
      payment_mode: "",
      transactionId: "", // 🔹 Transaction ID field
      amount_paid: "",
      creditNoteAmount: "", // 🔹 Credit Note Total
      creditNoteDate: "", // 🔹 Credit Note Date
      creditNoteRef: "", // 🔹 Credit Note Reference No.
      GSTIN: "",
      registrationType: "",
    },
    additionalDetails: {
      notes: "",
      returnReason: "",
      credit: "",
      returnDate: "",
    },
    createdBy: userData?.user,
  };

  const [formData, setFormData] = useState({ ...formObj });

  const clearForm = () => {
    setFormData({ ...formObj });
  };

  function toTitleCase(text) {
    return text?.replace(/\w\S*/g, function (word) {
      return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
    });
  }

  useEffect(() => {
    if (supplierData?.length && formData.supplier.supplierId) {
      const supplier = supplierData.find(
        (item) => item.supplierId === formData.supplier.supplierId
      );

      if (supplier) {
        setFormData((prevFormData) => ({
          ...prevFormData,
          supplier: {
            ...prevFormData.supplier,
            address: supplier.supplierAddress,
            GSTIN: supplier.GSTIN,
            contactNo: supplier.supplierContact,
            state: toTitleCase(supplier.state),
          },
        }));
      }
    }
  }, [supplierData, formData.supplier.supplierId]);

  useEffect(() => {
    if (validateDate(selectedDate)) {
      const nDate = new Date(selectedDate);
      const withoutHours = new Date(nDate.setHours(0, 0, 0, 0)).toISOString();
      setFormData((prev) => ({
        ...prev,
        paymentDetails: {
          ...prev.paymentDetails,
          invoice_date: withoutHours,
        },
      }));
    }
  }, [selectedDate]);

  useEffect(() => {
    if (
      validateDate(selectedRtnDate) &&
      formData.additionalDetails.returnReason
    ) {
      const nDate = new Date(selectedRtnDate);
      const withoutHours = new Date(nDate.setHours(0, 0, 0, 0)).toISOString();
      setFormData((prev) => ({
        ...prev,
        additionalDetails: {
          ...prev.additionalDetails,
          returnDate: withoutHours,
        },
      }));
    }
  }, [selectedRtnDate, formData.additionalDetails.returnReason]);

  useEffect(() => {
    if (mode === "Return Mode") {
      setSelectedRtnDate(formatDate(new Date()));
    }
  }, [mode]);
  useEffect(() => {
    if (editFormData && Object.keys(editFormData).length > 0) {
      setFormData(editFormData);
      setSelectedDate(() =>
        formatDate(editFormData.paymentDetails?.invoice_date || new Date())
      );

      const item = purchaseData?.find(
        (item) =>
          item.paymentDetails?.invoice_id === editFormData.invoice_id &&
          item.purchaseId === editFormData.purchaseId
      );
      if (item) {
        setSelectedDate(() => formatDate(item.paymentDetails.invoice_date));
      }
    }
  }, [editFormData, purchaseData]);

  useEffect(() => {
    if (userData?.token) {
      dispatch(
        getbranchData({ token: userData.token, branch: userData.branch })
      );
      dispatch(
        getInventorySupplier({ token: userData.token, branch: userData.branch })
      );
    }
  }, [userData?.token]);

  // Dynamic Pending Amount Calculation
  useEffect(() => {
    const total = parseFloat(formData.paymentDetails?.total_amount) || 0;
    const paid = parseFloat(formData.paymentDetails?.amount_paid) || 0;
    const creditNote = parseFloat(formData.paymentDetails?.creditNoteAmount) || 0;
    const pending = total - paid - creditNote;

    setFormData((prev) => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        pending_amount: pending.toFixed(2),
      },
    }));
  }, [
    formData.paymentDetails?.total_amount,
    formData.paymentDetails?.amount_paid,
    formData.paymentDetails?.creditNoteAmount,
  ]);

  // Handle numeric field changes safely
  const handleChange = (field, value) => {
    if (value !== "" && isNaN(value)) {
      setError("Please enter a valid number");
      return;
    }

    setError("");
    setFormData((prev) => {
      const updatedDetails = {
        ...prev.paymentDetails,
        [field]: value,
      };

      // Reset creditNoteRef and creditNoteDate if Credit Note Amount is 0 or emptied
      if (field === "creditNoteAmount" && (parseFloat(value) <= 0 || value === "")) {
        updatedDetails.creditNoteRef = "";
        updatedDetails.creditNoteDate = "";
      }

      return {
        ...prev,
        paymentDetails: updatedDetails,
      };
    });
  };

  // Dedicated Payment Mode Handler (clears transactionId if Cash)
  const handlePaymentModeChange = (modeValue) => {
    setFormData((prev) => ({
      ...prev,
      paymentDetails: {
        ...prev.paymentDetails,
        payment_mode: modeValue,
        // Auto-clear transaction ID if Cash is selected
        transactionId: modeValue === "Cash" ? "" : prev.paymentDetails?.transactionId || "",
      },
    }));
  };

  return (
    <Container fluid>
      <Card className="mb-5 mt-2 shadow-sm">
        <CardHeader>
          <Row className="d-flex justify-content-center">
            <Col md={3} style={{ textAlign: "center" }}>
              <h4>
                {mode === "Return Mode" ? "Debit Entry" : "Purchase Entry"}
              </h4>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <Form>
            {/* Supplier Information Row */}
            <Row>
              <Col md={5}>
                <FormGroup>
                  <Label for="supplier">Supplier</Label>
                  <Input
                    type="select"
                    name="supplierId"
                    id="supplier"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.supplierId || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          supplierId: e.target.value,
                        },
                      });
                    }}
                  >
                    <option value="">Select Supplier</option>
                    {supplierData?.length > 0 &&
                      supplierData.map((item) => (
                        <option key={item.supplierId} value={item.supplierId}>
                          {item.supplierName}
                        </option>
                      ))}
                  </Input>
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="invoice">Invoice No</Label>
                  <Input
                    type="text"
                    name="invoice_id"
                    id="invoice"
                    disabled={mode === "Return Mode"}
                    value={formData.paymentDetails?.invoice_id || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        paymentDetails: {
                          ...formData.paymentDetails,
                          invoice_id: e.target.value,
                        },
                      });
                    }}
                    placeholder="Enter Invoice No."
                  />
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="invoice-date">Invoice Date</Label>
                  <Input
                    type="date"
                    name="invoice_date"
                    id="invoice-date"
                    disabled={mode === "Return Mode"}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  />
                  {errorDate && <span style={{ color: "red" }}>{errorDate}</span>}
                </FormGroup>
              </Col>
            </Row>

            {/* Party & GST Details Row */}
            <Row>
              <Col md={2}>
                <FormGroup>
                  <Label for="party">Party Name</Label>
                  <Input
                    type="text"
                    name="partyName"
                    disabled={mode === "Return Mode"}
                    id="party"
                    value={formData.supplier?.partyName || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          partyName: e.target.value,
                        },
                      });
                    }}
                    placeholder="Enter Party Name"
                  />
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="taxInput">GSTIN</Label>
                  <Input
                    type="text"
                    name="GSTIN"
                    id="taxInput"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.GSTIN || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          GSTIN: e.target.value,
                        },
                      })
                    }
                  />
                </FormGroup>
              </Col>

              <Col md={5}>
                <FormGroup>
                  <Label for="tax">Address</Label>
                  <Input
                    type="text"
                    name="address"
                    disabled={mode === "Return Mode"}
                    id="tax"
                    value={formData.supplier?.address || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          address: e.target.value,
                        },
                      });
                    }}
                    placeholder="Enter Address"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="state">State</Label>
                  <Input
                    type="text"
                    name="state"
                    id="state"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.state || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          state: e.target.value,
                        },
                      });
                    }}
                    placeholder="State"
                  />
                </FormGroup>
              </Col>

              <Col md={3}>
                <FormGroup>
                  <Label for="contact">Contact No</Label>
                  <Input
                    type="text"
                    name="contactNo"
                    id="contact"
                    disabled={mode === "Return Mode"}
                    value={formData.supplier?.contactNo || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        supplier: {
                          ...formData.supplier,
                          contactNo: e.target.value,
                        },
                      });
                    }}
                    placeholder="Enter Contact No."
                  />
                </FormGroup>
              </Col>

              <Col md={4}>
                <FormGroup>
                  <Label for="hsn">GST Registration Type</Label>
                  <Input
                    type="select"
                    name="registrationType"
                    id="hsn"
                    disabled={mode === "Return Mode"}
                    value={formData.paymentDetails?.registrationType || ""}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        paymentDetails: {
                          ...formData.paymentDetails,
                          registrationType: e.target.value,
                        },
                      });
                    }}
                  >
                    {registrationType.map((option, id) => (
                      <option key={id} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {/* Payment & Financial Details Section */}
            <Row className="pb-3 border-top pt-3 mt-2">
              <Col md="2">
                <FormGroup>
                  <Label className="fw-bold">Total Amount</Label>
                  <Input
                    value={formData.paymentDetails?.total_amount || ""}
                    disabled={mode === "Return Mode"}
                    onChange={(e) => handleChange("total_amount", e.target.value)}
                  />
                  {error && <span style={{ color: "red" }}>{error}</span>}
                </FormGroup>
              </Col>

              <Col md="2">
                <FormGroup>
                  <Label className="fw-bold">Amount Paid</Label>
                  <Input
                    value={formData.paymentDetails?.amount_paid || ""}
                    disabled={mode === "Return Mode"}
                    onChange={(e) => handleChange("amount_paid", e.target.value)}
                  />
                </FormGroup>
              </Col>

              <Col md="2">
                <FormGroup>
                  <Label className="fw-bold">Payment Mode</Label>
                  <Input
                    id="paymode"
                    type="select"
                    disabled={mode === "Return Mode"}
                    value={formData.paymentDetails?.payment_mode || ""}
                    onChange={(e) => handlePaymentModeChange(e.target.value)}
                  >
                    <option value="">Select Mode</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Cheque">Cheque</option>
                    <option value="Net Banking">Net Banking</option>
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit">Credit</option>
                  </Input>
                </FormGroup>
              </Col>

              {/* Conditionally Render Transaction ID when mode is NOT Cash */}
              {formData.paymentDetails?.payment_mode &&
                formData.paymentDetails?.payment_mode !== "Cash" && (
                  <Col md="2">
                    <FormGroup>
                      <Label className="fw-bold text-primary">Transaction ID / Ref</Label>
                      <Input
                        type="text"
                        placeholder="Enter Ref/Txn ID"
                        disabled={mode === "Return Mode"}
                        value={formData.paymentDetails?.transactionId || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentDetails: {
                              ...formData.paymentDetails,
                              transactionId: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                )}

              <Col md="2">
                <FormGroup>
                  <Label className="fw-bold">Pending Amount</Label>
                  <Input
                    value={formData.paymentDetails?.pending_amount || ""}
                    disabled
                    style={{
                      fontWeight: "bold",
                      color:
                        parseFloat(formData.paymentDetails?.pending_amount) < 0
                          ? "green"
                          : "red",
                    }}
                  />
                </FormGroup>
              </Col>
            </Row>

            {/* Credit Note Adjustments Row */}
            <Row className="pb-3 border-top pt-2 bg-light rounded">
              <Col md="2">
                <FormGroup>
                  <Label className="fw-bold text-danger">Credit Note Total (₹)</Label>
                  <Input
                    value={formData.paymentDetails?.creditNoteAmount || ""}
                    disabled={mode === "Return Mode"}
                    placeholder="0.00"
                    onChange={(e) => handleChange("creditNoteAmount", e.target.value)}
                  />
                </FormGroup>
              </Col>

              {/* Show Credit Note Date and Ref only if Credit Note Amount > 0 */}
              {parseFloat(formData.paymentDetails?.creditNoteAmount) > 0 && (
                <>
                  <Col md="2">
                    <FormGroup>
                      <Label className="fw-bold text-danger">Credit Note Date</Label>
                      <Input
                        type="date"
                        value={formData.paymentDetails?.creditNoteDate || ""}
                        disabled={mode === "Return Mode"}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentDetails: {
                              ...formData.paymentDetails,
                              creditNoteDate: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md="3">
                    <FormGroup>
                      <Label className="fw-bold text-danger">Credit Note Ref No.</Label>
                      <Input
                        type="text"
                        placeholder="Enter CN Invoice/Doc No."
                        disabled={mode === "Return Mode"}
                        value={formData.paymentDetails?.creditNoteRef || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentDetails: {
                              ...formData.paymentDetails,
                              creditNoteRef: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>
                  </Col>
                </>
              )}

              {mode === "Return Mode" && (
                <>
                  <Col md="2">
                    <FormGroup>
                      <Label>Credit Amount</Label>
                      <Input
                        id="credit"
                        type="text"
                        name="credit"
                        disabled={mode !== "Return Mode"}
                        value={formData.additionalDetails?.credit || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            additionalDetails: {
                              ...formData.additionalDetails,
                              credit: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md="2">
                    <FormGroup>
                      <Label>Return Reason</Label>
                      <Input
                        id="return"
                        type="text"
                        name="returnReason"
                        disabled={mode !== "Return Mode"}
                        value={formData.additionalDetails?.returnReason || ""}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            additionalDetails: {
                              ...formData.additionalDetails,
                              returnReason: e.target.value,
                            },
                          })
                        }
                      />
                    </FormGroup>
                  </Col>

                  <Col md="3">
                    <FormGroup>
                      <Label for="invoice-rtn-date">Return Date</Label>
                      <Input
                        type="date"
                        name="invoice_rtn_date"
                        id="invoice-rtn-date"
                        value={selectedRtnDate}
                        onChange={(e) => setSelectedRtnDate(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                </>
              )}
            </Row>
          </Form>

          {/* Child Entry Component */}
          <PurchaseEntry
            purchaseInvoiceFormData={formData}
            selectedRtnDate={selectedRtnDate}
            clearSupplierForm={clearForm}
            setShowModal={setShowModal}
            togglePurchaseEntry={togglePurchaseEntry}
            mode={mode}
            itemsData={itemsData}
          />
        </CardBody>
      </Card>

      <Modal isOpen={showModal} toggle={setShowModal} centered>
        <ModalBody style={{ textAlign: "center", padding: "20px" }}>
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p style={{ marginTop: "10px", fontSize: "16px", fontWeight: "bold" }}>
            Saving...
          </p>
        </ModalBody>
      </Modal>
    </Container>
  );
};

export default PurchaseForm;